import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Stack } from '@fluentui/react'
import { ErrorCircleRegular } from '@fluentui/react-icons'
import uuid from 'react-uuid';
import {
  ChatHistoryLoadingState,
  ChatMessage,
  Citation,
  Conversation,
  conversationApi,
  ConversationRequest,
  CosmosDBStatus,
  historyUpdate,
  ToolMessageContent,
  BackendChatResponse
} from "../../api";
import { setCurrentConversationId } from "../../utils/userSession";
import { readFromSessionStorage, writeToSessionStorage } from "../../utils/localStorage"; 
import { Answer } from "./components/Answer";
import { QuestionInput } from "./components/QuestionInput";
import { AppStateContext } from "../../state/AppProvider";
import { useBoolean } from "@fluentui/react-hooks";
import { SpeechToTextArgs, useSpeech } from '../../hooks/useSpeech';
import { TalkingHeadContext, TalkingHeadContextType } from '../../state/TalkingHeadProvider'
import { AnimationsEnum } from '../../constants/talkingHead'
import { MessageStatusesEnum } from '../../constants/chat'
import { useDebug } from '../../hooks/useDebug';
import { Tooltip } from '../tooltip/Tooltip';
import SoundOff from '../../assets/soundOff.svg';
import SoundOn from '../../assets/soundOn.svg';
import styles from "./Chat.module.css";
import { useIsMobile } from '../../hooks/useIsMobile';
import { useKeyboard } from '../../hooks/useKeyboard';

interface ChatProps {   
  fullScreen?: boolean;
  hideTooltip: boolean;
  setHideTooltip: (hide: boolean) => void;
  toggleSpeaking: () => void;
  setAllowDefaultSpeech: (allow: boolean) => void;
  allowDefaultSpeech: boolean;
}

const defaultMessage: ChatMessage = {
    id: uuid(),
    role: "assistant",
    content: "How can I help you today?",
    date: new Date().toISOString(),
    is_finalized: true,
}

const SESSION_STORAGE_KEY = "leora_current_conversation_messages";

export const ChatList = ({fullScreen, hideTooltip, setHideTooltip, toggleSpeaking, allowDefaultSpeech, setAllowDefaultSpeech}: ChatProps) => {
    
    const isMobile = useIsMobile(900);
    const {isKeyboardOpen, keyboardHeight} = useKeyboard();
    
    const appStateContext = useContext(AppStateContext)
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const hasCitationsShown = appStateContext?.state.frontendSettings?.ui?.show_citations;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const isGeneratingAnswer = appStateContext?.state.isGeneratingAnswer;
    const abortFuncs = useRef([] as AbortController[]);
    const isInitialized = useRef(false);
    
    const [messages, setMessages] = useState<ChatMessage[]>(() => {
        const savedMessages = readFromSessionStorage(SESSION_STORAGE_KEY);
        if (savedMessages) {
            return Array.isArray(savedMessages) ? savedMessages : [];
        }
        return [];
    });
    
    const [processMessages, setProcessMessages] = useState<MessageStatusesEnum>(MessageStatusesEnum.NotRunning);
    const [hideErrorDialog, { toggle: toggleErrorDialog }] = useBoolean(true);
    const [wasStoppedGenerating, setStopGeneratingState] = useState<boolean>(false);

    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

    const debug = useDebug({ debugMode: appStateContext?.state.frontendSettings?.debug })
    const {
        voice: talkingHeadVoice,
        visemes: { addViseme },
        animation: {
        setAnimation: setAnimationForTalkingHead,
        think: setThinkingAnimationForTalkingHead,
        stopThinking: setStopThinkingAnimationForTalkingHead,
        listen: setListenAnimationForTalkingHead
        },
        talking: { isTalking, setTalking },
        speech: {
            isTextToSpeechMuted,
            setIsTextToSpeechMuted, 
        }
    } = useContext(TalkingHeadContext) as TalkingHeadContextType

    const {
        isSpeechEnabled,
        speechToTextStatus,
        textToSpeech,
        speechToText,
        terminateSpeechToText,
        terminateTextToSpeech
    } = useSpeech({
        onSpeechStarted: () => {
        debug(() => console.log("Speech started"))
        setTalking(true)
        },
        onSpeechFinished: () => {
        debug(() => console.log("Speech finished"))
        setTalking(false)
        },
        onVisemeReceived: (id, offset, visemeId, animation) => {
        debug(() => console.log("Viseme received: ", animation))
        addViseme({ id, visemeId, offset, animation })
        },
        isTextToSpeechMuted: isTextToSpeechMuted
    });

    const [ASSISTANT, TOOL, ERROR] = ["assistant", "tool", "error"]
    const NO_CONTENT_ERROR = "No content in messages object."

    const stopGenerating = () => {
        abortFuncs.current.forEach(a => a.abort());
        appStateContext?.dispatch({ type: 'SET_GENERATING_ANSWER_STATE', payload: false });
        setStopGeneratingState(true);
        setIsLoading(false);
      }

    useEffect(() => {
        if (appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.Working
        && appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured
        && appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Fail
        && hideErrorDialog) {
        toggleErrorDialog();
        }
    }, [appStateContext?.state.isCosmosDBAvailable]);

    useEffect(() => {
        setIsLoading(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading)
    }, [appStateContext?.state.chatHistoryLoadingState])

    const makeApiRequestWithoutCosmosDB = async (question: string, conversationId?: string) => {
        setIsLoading(true);
        appStateContext?.dispatch({ type: 'SET_GENERATING_ANSWER_STATE', payload: true });
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        const userMessage: ChatMessage = {
        id: uuid(),
        role: "user",
        content: question,
        date: new Date().toISOString(),
        is_finalized: true
        };

        let conversation: Conversation | null | undefined;
        
        // Use existing conversation or start new one
        conversation = appStateContext?.state?.currentChat;

        if (!conversation) {
            const savedMessages = readFromSessionStorage(SESSION_STORAGE_KEY); 
            let messageHistory: ChatMessage[] = [];

            if (Array.isArray(savedMessages)) {
                messageHistory = savedMessages;
            }

            conversation = {
                id: uuid(),
                title: "New Conversation",
                messages: [...messageHistory, userMessage],
                date: new Date().toISOString(),
            };
            
        } else {
            conversation.messages.push(userMessage);
        }

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
        setMessages(conversation.messages)

        const request: ConversationRequest = {
        messages: [...conversation.messages.filter((answer) => answer.role !== ERROR)]
        };

        try {
        setProcessMessages(MessageStatusesEnum.Requested);
        const response = await conversationApi(request, abortController.signal);

        if (!response.ok) {
            let errorMessage = "Sorry, the chat service is temporarily unavailable. Please try again later.";
            if (response.status === 401) {
                errorMessage = "Access denied. You don't have permission to view this chat.";
            } else if (response.status === 400) {
                errorMessage = "The assistant cannot process your message. Please rephrase your message and try again.";
            } else if (response.status === 500) {
                errorMessage = "Something went wrong on our end. Please try again later.";
            }

            errorMessage = parseErrorMessage(errorMessage);

            let errorChatMsg: ChatMessage = {
            id: uuid(),
            role: ERROR,
            content: errorMessage,
            date: new Date().toISOString(),
            is_finalized: true
            }
            conversation.messages.push(errorChatMsg);
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
            setMessages([ ...messages, errorChatMsg]);
            setIsTextToSpeechMuted((prevState) => {
                if (!prevState) {
                    setTalking(true)
                    textToSpeech({ text: errorMessage, voice: talkingHeadVoice })
                }
                return prevState
            })
            return;
        }

        // Parse backend response directly
        const backendResponse: BackendChatResponse = await response.json();
        
        // Create assistant message from backend response
        const assistantMessage: ChatMessage = {
            id: uuid(),
            role: "assistant",
            content: backendResponse.response,
            date: new Date().toISOString(),
            is_finalized: true
        };

        // Update conversation with backend data
        conversation.id = backendResponse.conversationId;
        conversation.title = backendResponse.title;
        conversation.date = backendResponse.timestamp;
        
        // Store conversationId in session storage for future messages
        setCurrentConversationId(backendResponse.conversationId);

        conversation.messages.push(assistantMessage);
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
        setMessages([...messages, assistantMessage]);
        
        setIsTextToSpeechMuted((prevState) => {
            if (!prevState) {
                setTalking(true)
                const textForSpeech = assistantMessage.content.replace(markdownLinkRegex, '');
                textToSpeech({ text: textForSpeech, voice: talkingHeadVoice })
            }
            return prevState
        })

        } catch (e) {
            console.log('Backend API error:', e)
        if (!abortController.signal.aborted) {
            let errorMessage = "An error occurred. Please try again. If the problem persists, please contact the site administrator.";
            
            if (e instanceof Error) {
                errorMessage = e.message;
            }

            errorMessage = parseErrorMessage(errorMessage);

            let errorChatMsg: ChatMessage = {
            id: uuid(),
            role: ERROR,
            content: errorMessage,
            date: new Date().toISOString(),
            is_finalized: true
            }
            conversation.messages.push(errorChatMsg);
            appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: conversation });
            setMessages([ ...messages, errorChatMsg]);
        } else {
            setMessages([ ...messages, userMessage])
        }
        } finally {
        setIsLoading(false);
        appStateContext?.dispatch({ type: 'SET_GENERATING_ANSWER_STATE', payload: false });
        abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
        setProcessMessages(MessageStatusesEnum.Done)
        }

        return abortController.abort();
    };

    const parseErrorMessage = (errorMessage: string) => {
        let errorCodeMessage = errorMessage.substring(0, errorMessage.indexOf("-") + 1);
        const innerErrorCue = "{\\'error\\': {\\'message\\': ";
        if (errorMessage.includes(innerErrorCue)) {
        try {
            let innerErrorString = errorMessage.substring(errorMessage.indexOf(innerErrorCue));
            if (innerErrorString.endsWith("'}}")) {
            innerErrorString = innerErrorString.substring(0, innerErrorString.length - 3);
            }
            innerErrorString = innerErrorString.replaceAll("\\'", "'")
            let newErrorMessage = errorCodeMessage + " " + innerErrorString;
            errorMessage = newErrorMessage;

        } catch (e) {
            console.error("Error parsing inner error message: ", e);
        }
        }
        return errorMessage;
    };

    const sendQuestion = (question: string, conversationId?: string) => {
        debug(() => console.log("Message sent"))
        terminateTextToSpeech()

        // Always use the backend API now (no CosmosDB check needed)
        makeApiRequestWithoutCosmosDB(question)
    }

    const stopListening = () => {
        setAnimationForTalkingHead(AnimationsEnum.ThinkStarted)
        terminateSpeechToText()
    }

    const startListening = (props: SpeechToTextArgs) => {
        setListenAnimationForTalkingHead()
        speechToText(props)
    }

    const onShowCitation = (citation: Citation) => {
        debug(() => console.log(console.log("Show citation clicked: ", citation)))
    }

    const switchSpeaking = () => {
        toggleSpeaking()
    }

    useEffect(() => {
        if (appStateContext?.state.currentChat) {
            setMessages(appStateContext.state.currentChat.messages);
            isInitialized.current = true;
        } else {
            if (isInitialized.current) {
                setMessages([]);
            }
        }
    }, [appStateContext?.state.currentChat]);

    useLayoutEffect(() => {
        const saveToDB = async (messages: ChatMessage[], id: string) => {
        const response = await historyUpdate(messages, id)
        return response
        }

        if (wasStoppedGenerating) return

        if (appStateContext && appStateContext.state.currentChat && processMessages === MessageStatusesEnum.Done) {
        if (appStateContext.state.isCosmosDBAvailable.cosmosDB) {
            if (!appStateContext?.state.currentChat?.messages) {
            console.error("Failure fetching current chat state.")
            return
            }
            const noContentError = appStateContext.state.currentChat.messages.find(m => m.role === ERROR)

            if (!noContentError?.content.includes(NO_CONTENT_ERROR)) {
            saveToDB(appStateContext.state.currentChat.messages, appStateContext.state.currentChat.id)
                .then((res) => {
                if (!res.ok) {
                    let errorMessage = "An error occurred. Answers can't be saved at this time. If the problem persists, please contact the site administrator.";
                    let errorChatMsg: ChatMessage = {
                    id: uuid(),
                    role: ERROR,
                    content: errorMessage,
                    date: new Date().toISOString(),
                    is_finalized: true
                    }
                    if (!appStateContext?.state.currentChat?.messages) {
                    let err: Error = {
                        ...new Error,
                        message: "Failure fetching current chat state."
                    }
                    throw err
                    }
                    setMessages([ ...appStateContext?.state.currentChat?.messages, errorChatMsg])
                }
                return res as Response
                })
                .catch((err) => {
                console.error("Error: ", err)
                let errRes: Response = {
                    ...new Response,
                    ok: false,
                    status: 500,
                }
                return errRes;
                })
            }
        }
        appStateContext?.dispatch({ type: 'UPDATE_CHAT_HISTORY', payload: appStateContext.state.currentChat });
        setMessages(appStateContext.state.currentChat.messages)
        setProcessMessages(MessageStatusesEnum.NotRunning)
        }
    }, [processMessages]);

    useLayoutEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" })
    }, [isGeneratingAnswer, processMessages]);

    const parseCitationFromMessage = (message: ChatMessage) => {
        if (message?.role && message?.role === "tool") {
        try {
            const toolMessage = JSON.parse(message.content) as ToolMessageContent;
            return toolMessage.citations;
        }
        catch {
            return [];
        }
        }
        return [];
    }

    useEffect(() => {
        debug(() => console.log("Answer is generating", isGeneratingAnswer))

        isGeneratingAnswer ? setThinkingAnimationForTalkingHead() : setStopThinkingAnimationForTalkingHead()
    }, [isGeneratingAnswer])

    useEffect(() => {
        if (!isTextToSpeechMuted && messages?.length === 0 && allowDefaultSpeech) {
            textToSpeech({ text: "Hi! I'm Leora, your AI assistant. How can I help you today?", voice: talkingHeadVoice })
        }
    }, [isTextToSpeechMuted])

    useEffect(() => {
        // Save messages to Session Storage
        writeToSessionStorage(SESSION_STORAGE_KEY, messages);
    }, [messages]);
      
    return (
        <div 
            className={fullScreen ? styles.chatContainer : styles.chatContainerSmall}
            style={isMobile && isKeyboardOpen ? {marginTop: `calc(25vh + ${keyboardHeight}px)`} : {}}
        >
            <div id="chat-overlay"  className={styles.chatMessageStream} role="log">
                <div className={styles.chatMessageGpt}>
                    <Answer
                        answer={{
                        answer: defaultMessage.content,
                        citations: [],
                        message_id: defaultMessage.id,
                        feedback: defaultMessage.feedback,
                        is_finalized: defaultMessage.is_finalized
                        }}
                        isFirstMessage
                        onCitationClicked={c => {onShowCitation(c)}}
                    />
                </div>
                {messages?.map((answer, index) => (
                    <React.Fragment key={index}>
                        {answer.role === "user" ? (
                        <div className={styles.chatMessageUser}>
                            <div className={styles.chatMessageUserMessage}>{answer.content}</div>
                        </div>
                        ) : (
                        answer.role === "assistant" ? <div className={styles.chatMessageGpt}>
                            <Answer
                            answer={{
                                answer: answer.content,
                                citations: hasCitationsShown ? parseCitationFromMessage(messages[index - 1]) : [],
                                message_id: answer.id,
                                feedback: answer.feedback,
                                is_finalized: index !== messages.length-1 || answer.is_finalized || !isGeneratingAnswer
                            }}
                            onCitationClicked={c => {onShowCitation(c)}}
                            />
                        </div> : answer.role === ERROR ? <div className={styles.chatMessageError}>
                            <Stack horizontal className={styles.chatMessageErrorContent}>
                            <ErrorCircleRegular className={styles.errorIcon} style={{ color: "rgba(255, 120, 120, 1)" }} />
                            <span>Error</span>
                            </Stack>
                            <span className={styles.chatMessageErrorContent}>{answer.content}</span>
                        </div> : null
                        )}
                    </React.Fragment>
                ))}
                <div ref={chatMessageStreamEnd} />
            </div>

            <Stack 
                horizontal 
                className={styles.chatInput}
            >
                {isSpeechEnabled && !isMobile &&
                    <div className={styles.iconWrapper}>
                        {!hideTooltip && isTextToSpeechMuted && <Tooltip 
                            text="Click to let me speak up! I'm ready to chat!"
                            onClick={() => setHideTooltip(true)}
                            isTop={!isMobile}
                            right={35}
                            bottom={35}
                            width={195}
                            padding={'10px'}
                            margin={'0px 0px 10px'}
                        />}
                        <button className={styles.actionButton}
                        aria-label="Allow speech button"
                        onClick={switchSpeaking}
                        >
                            {isTextToSpeechMuted ?
                                <img src={SoundOff} alt="sound off" className={styles.iconButton} />
                                :
                                <img src={SoundOn} alt="sound on" className={styles.iconButton} />
                            }
                        </button>
                    </div>
                }
            <QuestionInput
                clearOnSend
                placeholder={isGeneratingAnswer && processMessages == MessageStatusesEnum.Requested ? "Generating answer..." : "Type a new question..."}
                disabled={isLoading}
                isSpeechEnabled={isSpeechEnabled}
                isGeneratingAnswer={isGeneratingAnswer}
                speechToTextStatus={speechToTextStatus}
                onSend={sendQuestion}
                onStopGenerating={stopGenerating}
                conversationId={undefined} // Conversation ID is now managed by user session
                emitSpeechToText={startListening}
                emitTerminateSpeechToText={stopListening}
            />
            </Stack>
        </div>
    )
}